import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'
import { logEvent } from '../../analytics/utils/firebaseUtils'
import Button from '../../components/actions/Button'
import StringInput from '../../components/inputs/StringInput'
import { AppContext } from '../../contexts/AppContext'
import { useLogin } from '../../contexts/hooks/useLogin'
import PublicWrapper from '../../wrappers/publicWrapper/PublicWrapper'
import './Login.scss'

const Login: React.FC = () => {
	const { isNativePlatform } = useContext(AppContext)

	const [loading, setLoading] = useState(false)
	const [emailInput, setEmailInput] = useState('')
	const [passwordInput, setPasswordInput] = useState('')
	const [loginError, setLoginError] = useState<'credentials' | 'server'>()

	const { t } = useTranslation('pages_login')
	const history = useHistory()
	const { login } = useLogin()

	const loginHandler = async () => {
		setLoading(true)
		setLoginError(undefined) // Clear previous errors
		logEvent('click-user-restore-password')

		const timeout = setTimeout(() => {
			setLoginError('server')
			setLoading(false)
		}, 10 * 1000) // 10 second timeout for server response

		try {
			await login(emailInput, passwordInput)
			setLoginError(undefined) // Clear any previous errors on success
			history.push('/overview') // Redirect to home page on successful login
		} catch (error) {
			setLoginError('credentials')
		} finally {
			clearTimeout(timeout)
			setLoading(false)
		}
	}

	return (
		<>
			<PublicWrapper>
				<h2 className='p-0 m-0 text-text-dark dark:text-white text-xl'>{t('otsikko')}</h2>

				{loginError === 'credentials' && (
					<div className='text-center text-highlight'>{t('kirjautuminenError')}</div>
				)}
				{loginError === 'server' && (
					<div className='text-center text-highlight'>{t('yhteysvirhe')}</div>
				)}

				<div className='flex flex-col py-[1.7rem]'>
					<StringInput
						label={t('sahkoposti', { ns: 'common' })}
						value={emailInput}
						onChange={val => setEmailInput(val)}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								loginHandler()
							}
						}}
					/>
				</div>
				<div className='input-container'>
					<StringInput
						type='password'
						label={t('salasana', { ns: 'common' })}
						value={passwordInput}
						onChange={val => setPasswordInput(val)}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								loginHandler()
							}
						}}
					/>
				</div>

				{!isNativePlatform ? (
					<>
						<div className='flex flex-row justify-end items-center mt-2'>
							<Link
								className='text-[#52AE32] hover:text-[#CAE3CC] dark:text-[#3CB49F] dark:hover:text-[#ACD7D4] underline cursor-pointer'
								to='/forgotten-password'
							>
								{t('forgorPassword')}
							</Link>
						</div>
					</>
				) : (
					<div>
						<div className='text-text-grey-200 dark:text-text-grey-100 text-center'>
							<Button className='hollow' routerLink='/forgotten-password'>
								{t('pyydaUusi')}
							</Button>
						</div>
					</div>
				)}

				<div className='flex flex-col gap-2 mt-4'>
					<Button disabled={loading} className='m-0' onClick={loginHandler}>
						{t('kirjaudu')}
					</Button>

					<Button className='hollow m-0' routerLink='/'>
						{t('alkuun')}
					</Button>
				</div>
			</PublicWrapper>
		</>
	)
}

export default Login
